import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Only create client if credentials are configured
export const supabase = (supabaseUrl && supabaseAnonKey) 
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

// Is this email allowed to sign in?
//
// Two lists. allowed_domains admits everyone at an organisation (iteach.net,
// k12coalition.com). allowed_emails admits one named person — the way to let an
// outside partner in without opening the door to their whole company.
export const checkDomainAllowed = async (email) => {
  if (!supabase) return false;
  const normalised = (email || '').trim().toLowerCase();
  const domain = normalised.split('@')[1];
  if (!domain) return false;

  const { data: byDomain } = await supabase
    .from('allowed_domains')
    .select('domain')
    .eq('domain', domain)
    .maybeSingle();
  if (byDomain) return true;

  const { data: byEmail } = await supabase
    .from('allowed_emails')
    .select('email')
    .eq('email', normalised)
    .maybeSingle();
  return !!byEmail;
};

// Upsert user on login (create or update)
export const upsertUser = async (email, displayName, avatarUrl) => {
  if (!supabase) return;
  const domain = email.split('@')[1]?.toLowerCase();
  
  // Check if user exists
  const { data: existing } = await supabase
    .from('users')
    .select('login_count')
    .eq('email', email)
    .single();
  
  if (existing) {
    await supabase
      .from('users')
      .update({ 
        last_login: new Date().toISOString(),
        login_count: (existing.login_count || 0) + 1,
        display_name: displayName,
        avatar_url: avatarUrl
      })
      .eq('email', email);
  } else {
    await supabase
      .from('users')
      .insert({ 
        email, 
        display_name: displayName, 
        avatar_url: avatarUrl,
        domain 
      });
  }
};

// Log a user activity
export const logActivity = async (userEmail, action, details = {}) => {
  if (!supabase) return;
  try {
    await supabase
      .from('activity_log')
      .insert({ 
        user_email: userEmail, 
        action, 
        details 
      });
  } catch (e) {
    console.warn('Activity log failed:', e);
  }
};

// Get user preferences (card config)
export const getUserPreferences = async (email) => {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('card_config, default_state')
      .eq('email', email)
      .single();
    if (error || !data) return null;
    return data;
  } catch (e) {
    return null;
  }
};

// Save user preferences
export const saveUserPreferences = async (email, cardConfig, defaultState) => {
  if (!supabase) return;
  try {
    const { data: existing } = await supabase
      .from('user_preferences')
      .select('id')
      .eq('email', email)
      .single();
    
    if (existing) {
      await supabase
        .from('user_preferences')
        .update({ 
          card_config: cardConfig, 
          default_state: defaultState,
          updated_at: new Date().toISOString() 
        })
        .eq('email', email);
    } else {
      await supabase
        .from('user_preferences')
        .insert({ 
          email, 
          card_config: cardConfig, 
          default_state: defaultState 
        });
    }
  } catch (e) {
    console.warn('Save preferences failed:', e);
  }
};

// Check if user is admin
export const checkIsAdmin = async (email) => {
  if (!supabase) return false;
  try {
    const { data } = await supabase
      .from('users')
      .select('is_admin')
      .eq('email', email)
      .single();
    return data?.is_admin === true;
  } catch (e) {
    return false;
  }
};

// Get dashboard access list for a user
export const getUserDashboardAccess = async (email) => {
  if (!supabase) return ['funnel']; // default fallback
  try {
    const { data, error } = await supabase
      .from('users')
      .select('dashboard_access')
      .eq('email', email)
      .single();
    if (error || !data || !data.dashboard_access) return ['funnel'];
    return data.dashboard_access;
  } catch (e) {
    return ['funnel'];
  }
};

// Update dashboard access for a user (admin only)
export const setUserDashboardAccess = async (email, dashboards) => {
  if (!supabase) return false;
  try {
    const { error } = await supabase
      .from('users')
      .update({ dashboard_access: dashboards })
      .eq('email', email);
    return !error;
  } catch (e) {
    console.warn('Set dashboard access failed:', e);
    return false;
  }
};

// Fetch all admin analytics data
export const fetchAdminAnalytics = async () => {
  if (!supabase) return null;
  try {
    // Get all users
    const { data: users } = await supabase
      .from('users')
      .select('*')
      .order('last_login', { ascending: false });

    // Get all activity logs (last 90 days)
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    
    const { data: activity } = await supabase
      .from('activity_log')
      .select('*')
      .gte('created_at', ninetyDaysAgo.toISOString())
      .order('created_at', { ascending: false });

    // Get all user preferences
    const { data: preferences } = await supabase
      .from('user_preferences')
      .select('*');

    return { users: users || [], activity: activity || [], preferences: preferences || [] };
  } catch (e) {
    console.warn('Admin analytics fetch failed:', e);
    return null;
  }
};
